// Searchable country picker for the billing address: type a few letters of
// the country's name (or its two-letter code) instead of scrolling ~250 rows.

import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { COUNTRIES, countryName } from "@/lib/countries";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

export function CountryPicker({
  id,
  value,
  onChange,
  invalid,
}: {
  id?: string;
  /** ISO alpha-2 code, or "" for none. */
  value: string;
  onChange: (code: string) => void;
  invalid?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid || undefined}
          className="w-full justify-between gap-2 border-input bg-background px-3 font-normal dark:bg-input/30"
        >
          {value ? <span className="truncate">{countryName(value)}</span> : <span className="truncate text-muted-foreground">Choose a country</span>}
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(22rem,calc(100vw-2rem))] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search countries" />
          <CommandList>
            <CommandEmpty>No country matches.</CommandEmpty>
            {COUNTRIES.map((c) => (
              <CommandItem
                key={c.code}
                value={c.code}
                keywords={[c.name]}
                onSelect={() => {
                  onChange(c.code);
                  setOpen(false);
                }}
              >
                <span className="flex-1 truncate">{c.name}</span>
                <Check className={cn("size-4 shrink-0", value === c.code ? "opacity-100" : "opacity-0")} />
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
